import React from 'react'
import {selectTheme} from '../../store'

export type QuestionnaireSliderPreviewProps = {
  questionText: string
  minValue: number
  maxValue: number
  selectedValue: number
  minLabel: string
  maxLabel: string
  selectedOptionLabel?: string
  showAnswer?: boolean
}

export const QuestionnaireSliderPreview = (props: QuestionnaireSliderPreviewProps) => {
  const {color, fontSize, fontWeight} = selectTheme()
  const {questionText, minValue, maxValue, selectedValue, minLabel, maxLabel, selectedOptionLabel, showAnswer} = props

  const formatPreviewNumber = (value: number) => {
    return Number.isInteger(value) ? `${value}` : `${value.toFixed(1).replace(/\.0$/, '')}`
  }

  const getPreviewThumbPercent = () => {
    if (minValue === maxValue) return 50
    const percent = ((selectedValue - minValue) / (maxValue - minValue)) * 100
    return Math.min(100, Math.max(0, percent))
  }

  const thumbPercent = getPreviewThumbPercent()
  const formattedSelectedValue = formatPreviewNumber(selectedValue)
  const answerText = selectedOptionLabel ? `${formattedSelectedValue} - ${selectedOptionLabel}` : formattedSelectedValue

  return (
    <div
      css={{
        width: '280px',
        minWidth: '280px',
        minHeight: showAnswer ? '230px' : '170px',
        border: `1px solid ${color.border.default}`,
        borderRadius: '8px',
        backgroundColor: color.surface.default,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxSizing: 'border-box',
      }}>
      <div
        css={{
          width: '100%',
          padding: '4px 12px',
          backgroundColor: color.surface.nested,
          textAlign: 'center',
          color: color.textIcon.secondary,
          fontSize: fontSize.h7,
          fontWeight: fontWeight.bold,
          boxSizing: 'border-box',
        }}>
        Example of participant app
      </div>
      <div
        css={{
          width: '100%',
          padding: '16px 28px 13px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: showAnswer ? '16px' : '24px',
          boxSizing: 'border-box',
        }}>
        <p
          css={{
            width: '100%',
            margin: 0,
            color: color.textIcon.title,
            fontSize: fontSize.h5,
            fontWeight: fontWeight.bold,
            lineHeight: 'normal',
            textAlign: 'center',
            overflowWrap: 'anywhere',
          }}>
          {questionText}
        </p>
        {showAnswer && (
          <div
            css={{
              textAlign: 'center',
              fontSize: fontSize.h7,
              fontWeight: fontWeight.bold,
              lineHeight: 'normal',
            }}>
            <p css={{margin: 0, color: color.textIcon.secondary}}>Your answer:</p>
            <p css={{margin: 0, color: color.textIcon.link, overflowWrap: 'anywhere'}}>{answerText}</p>
          </div>
        )}
        <div
          css={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
          <div
            css={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              fontWeight: fontWeight.bold,
              lineHeight: 'normal',
            }}>
            <p css={{margin: 0, color: color.textIcon.greyIcon, fontSize: fontSize.h7}}>
              {formatPreviewNumber(minValue)}
            </p>
            <p css={{margin: 0, color: color.textIcon.link, fontSize: fontSize.h5}}>{formattedSelectedValue}</p>
            <p css={{margin: 0, color: color.textIcon.greyIcon, fontSize: fontSize.h7}}>
              {formatPreviewNumber(maxValue)}
            </p>
          </div>
          <div
            css={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}>
            <div
              css={{
                height: '24px',
                position: 'relative',
              }}>
              <div
                css={{
                  height: '4px',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '10px',
                  borderRadius: '999px',
                  backgroundColor: '#9aaaff',
                }}
              />
              <div
                css={{
                  width: '24px',
                  height: '24px',
                  position: 'absolute',
                  top: 0,
                  left: `clamp(0px, calc(${thumbPercent}% - 12px), calc(100% - 24px))`,
                  borderRadius: '50%',
                  border: `3px solid ${color.textIcon.link}`,
                  backgroundColor: color.surface.white,
                  boxSizing: 'border-box',
                  boxShadow: `inset 0 0 0 4px ${color.surface.white}`,
                }}>
                <div
                  css={{
                    width: '8px',
                    height: '8px',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    borderRadius: '50%',
                    backgroundColor: color.textIcon.link,
                  }}
                />
              </div>
            </div>
            <div
              css={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                color: color.textIcon.title,
                fontSize: fontSize.h7,
                fontWeight: fontWeight.bold,
                lineHeight: 'normal',
              }}>
              <p
                css={{
                  width: '96px',
                  margin: 0,
                  overflowWrap: 'anywhere',
                }}>
                {minLabel}
              </p>
              <p
                css={{
                  width: '96px',
                  margin: 0,
                  overflowWrap: 'anywhere',
                  textAlign: 'right',
                }}>
                {maxLabel}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
